/**
 * qnet.ts — Linear Q-function, fit by closed-form ridge regression.
 *
 * This is the FROZEN-TARGET approximator for Fitted Q-Iteration (see fqi.ts).
 * Why linear (not the old GRU):
 *   - The previous architecture (qnet.ts v1) was a GRU + Q-head trained by
 *     online TD(λ). It diverged — the deadly triad (off-policy + bootstrapping
 *     + non-linear function approximation) made Q-values explode to 1e51, and
 *     eval got WORSE with training. Target networks, δ-clipping, gradient
 *     clipping, λ tuning — none of it saved the architecture.
 *   - A LINEAR Q fit by ridge regression CANNOT diverge: each fit is the
 *     exact minimizer of a convex least-squares problem with a fixed target.
 *     The moving-target instability of off-policy TD is gone.
 *   - On the 4-world × 3-seed validation, linear-FQI beats random by
 *     1.31–1.54× on every (world, seed). The features are informative enough
 *     that the loss of expressiveness vs an MLP doesn't matter; the stability
 *     gain does.
 *
 * Q(s, a) = w · φ(s, a) + b    where φ ∈ ℝ^N_FEATURES (11 features per action).
 * Weights live in a single Float64Array of length N_FEATURES + 1 (last entry
 * is the bias). Predicting one action's Q is a single dot product; predicting
 * all 32 candidates is a matvec over a (32 × 12) feature block.
 */

import { choleskySolve } from './linalg';
import { N_FEATURES, N_ACTIONS } from './features';

/** Length of the weight vector (features + bias). */
export const N_W = N_FEATURES + 1;

/** Default L2 regularisation strength for the ridge solve. */
export const DEFAULT_LAMBDA = 1.0;
/** Default discount factor for the bootstrap target r + γ·maxQ'. */
export const DEFAULT_GAMMA = 0.95;

export interface LinearQOptions {
  /** Ridge λ (L2 strength). Larger = more regularisation, smoother Q. */
  lambda?: number;
  /** Discount γ for the FQI target. The agent's effective planning horizon. */
  gamma?: number;
}

export class LinearQ {
  /** Weight vector — features then bias. Length N_FEATURES + 1. */
  w: Float64Array;
  /** Ridge regularisation strength (read at fit-time; mutating is safe). */
  lambda: number;
  /** Discount factor (read by fqi.ts when computing bootstrap targets). */
  gamma: number;

  constructor(opts: LinearQOptions = {}) {
    this.w = new Float64Array(N_W);
    this.lambda = opts.lambda ?? DEFAULT_LAMBDA;
    this.gamma = opts.gamma ?? DEFAULT_GAMMA;
  }

  /**
   * Predict Q for one action's feature row. `x` must be length N_FEATURES.
   * Augments x with a trailing 1 (the bias term) and dots with w.
   */
  predict(x: Float64Array): number {
    let s = this.w[N_FEATURES]; // bias
    for (let i = 0; i < N_FEATURES; i++) s += this.w[i] * x[i];
    return s;
  }

  /**
   * Predict Q for all N_ACTIONS candidates at once.
   * `Xall` is a flat (N_ACTIONS × N_FEATURES) block, row-major. Returns a
   * freshly-allocated Float64Array(N_ACTIONS). Use predictInto() to reuse a
   * buffer.
   */
  predictActions(Xall: Float64Array): Float64Array {
    const out = new Float64Array(N_ACTIONS);
    return this.predictInto(Xall, out);
  }

  /** In-place version of predictActions. `out` must be length N_ACTIONS. */
  predictInto(Xall: Float64Array, out: Float64Array): Float64Array {
    const b = this.w[N_FEATURES];
    for (let a = 0; a < N_ACTIONS; a++) {
      const row = a * N_FEATURES;
      let s = b;
      for (let i = 0; i < N_FEATURES; i++) s += this.w[i] * Xall[row + i];
      out[a] = s;
    }
    return out;
  }

  /**
   * Predict Q for every candidate across a BATCH of next-states — used by
   * fqi.ts to compute max_a' Q(s', a') for the bootstrap target. `Xbatch`
   * is (B × N_ACTIONS × N_FEATURES) flat; `out` is (B × N_ACTIONS), filled
   * row-major.
   */
  predictBatch(Xbatch: Float64Array, B: number, out: Float64Array): Float64Array {
    const b = this.w[N_FEATURES];
    const stride = N_ACTIONS * N_FEATURES;
    for (let k = 0; k < B; k++) {
      const xOff = k * stride;
      const oOff = k * N_ACTIONS;
      for (let a = 0; a < N_ACTIONS; a++) {
        const row = xOff + a * N_FEATURES;
        let s = b;
        for (let i = 0; i < N_FEATURES; i++) s += this.w[i] * Xbatch[row + i];
        out[oOff + a] = s;
      }
    }
    return out;
  }

  /**
   * Fit the weights to (X, y) by ridge regression:
   *
   *   w = (AᵀA + λI)⁻¹ Aᵀy,    A = [X | 1]  (the bias column appended).
   *
   * The closed-form solution is exact — there is no learning rate, no
   * iteration, no divergence. The whole "fit a round" call is one Cholesky
   * solve of a 12×12 system (N_FEATURES+1=12), which takes ~50µs.
   *
   * `X` is (B × N_FEATURES) flat; `y` is length B.
   */
  fit(X: Float64Array, y: Float64Array, B: number): void {
    const d = N_W;
    // Build the Gram matrix G = AᵀA + λI  (d×d) and vector AtA_y = Aᵀy.
    const G = new Float64Array(d * d);
    const Ay = new Float64Array(d);
    // Accumulate AᵀA: for each row of A (the feature row + trailing 1),
    // do the outer product into G, and accumulate Aᵀy.
    for (let k = 0; k < B; k++) {
      const off = k * N_FEATURES;
      // We build the augmented row inline to avoid allocating it.
      // Augmented row entries 0..N_FEATURES-1 = X[off..], entry N_FEATURES = 1.
      // Diagonal of the bias block:
      // G[i,j] += A[i]*A[j] for i,j in [0, N_FEATURES-1] using X[off+i].
      for (let i = 0; i < N_FEATURES; i++) {
        const xi = X[off + i];
        if (xi === 0) continue; // sparse-friendly skip; features are often 0
        const rowOff = i * d;
        for (let j = 0; j < N_FEATURES; j++) {
          G[rowOff + j] += xi * X[off + j];
        }
        // Cross terms with the bias column (A[N_FEATURES] = 1):
        G[rowOff + N_FEATURES] += xi;
        // Aᵀy contribution from feature i.
        Ay[i] += xi * y[k];
      }
      // Bias diagonal: G[N_FEATURES, N_FEATURES] += 1*1.
      G[N_FEATURES * d + N_FEATURES] += 1;
      // Cross terms (other half — symmetric): G[N_FEATURES, i] += 1 * X[i].
      const biasRow = N_FEATURES * d;
      for (let j = 0; j < N_FEATURES; j++) G[biasRow + j] += X[off + j];
      // Aᵀy bias contribution.
      Ay[N_FEATURES] += y[k];
    }
    // Add λI (ridge). Don't regularise the bias (standard convention) — or
    // do it lightly. We regularise only the feature weights.
    for (let i = 0; i < N_FEATURES; i++) G[i * d + i] += this.lambda;

    // Solve G w = Ay via Cholesky. G is SPD (AᵀA is PSD, +λI makes it PD).
    const sol = choleskySolve(G, Ay, d);
    this.w.set(sol);
  }

  /** Reset weights to zero (untrained). */
  reset(): void {
    this.w.fill(0);
  }

  /** Export weights as a plain number[] (for save/JSON). Length N_FEATURES+1. */
  exportWeights(): number[] {
    return Array.from(this.w);
  }

  /** Load weights from a number[] of length N_FEATURES+1. Throws on mismatch. */
  loadWeights(w: ArrayLike<number>): void {
    if (w.length !== N_W) {
      throw new Error(
        `loadWeights: expected length ${N_W}, got ${w.length} — layout drift`,
      );
    }
    for (let i = 0; i < N_W; i++) this.w[i] = w[i];
  }
}
